// Розсилка повідомлень усім користувачам через бота.
// Спершу пропонує "Тестова відправка собі", потім — реальна розсилка з підтвердженням.

import { useState } from 'react';
import { api, ApiError, type BroadcastResult } from '../../lib/api';
import { haptic } from '../../lib/telegram';

const BOT_USERNAME = import.meta.env.VITE_BOT_USERNAME ?? 'pdr_testy_bot';

export function AdminBroadcast() {
  const [text, setText] = useState('');
  const [parseMode, setParseMode] = useState<'HTML' | 'Markdown' | 'plain'>('plain');
  const [withButton, setWithButton] = useState(false);
  const [buttonText, setButtonText] = useState('🚀 Відкрити додаток');
  const [buttonUrl, setButtonUrl] = useState(`https://t.me/${BOT_USERNAME}/app`);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BroadcastResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const charCount = text.length;
  const tooLong = charCount > 4096;

  async function send(testOnly: boolean) {
    if (!text.trim() || tooLong || busy) return;
    if (!testOnly && !confirm(`Розіслати повідомлення ВСІМ користувачам бота? Скасувати неможливо.`)) {
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    haptic.medium();
    try {
      const res = await api.admin.broadcast({
        text,
        parseMode: parseMode === 'plain' ? undefined : parseMode,
        button: withButton && buttonUrl ? { text: buttonText, url: buttonUrl } : undefined,
        testOnly,
      });
      setResult(res);
      if (res.failed > 0) haptic.warning();
      else haptic.success();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Помилка');
      haptic.error();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 p-4">
      <section className="card space-y-2">
        <label className="block text-xs font-medium text-tg-hint">
          Повідомлення (макс. 4096 символів)
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          placeholder="Привіт! 👋&#10;&#10;Ми додали нові фічі..."
          className="w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
        />
        <div className="flex items-center justify-between text-xs">
          <span className={tooLong ? 'text-tg-destructive' : 'text-tg-hint'}>
            {charCount}/4096
          </span>
          <select
            value={parseMode}
            onChange={(e) => setParseMode(e.target.value as 'HTML' | 'Markdown' | 'plain')}
            className="rounded bg-tg-bg px-2 py-1 text-xs"
          >
            <option value="plain">Звичайний текст</option>
            <option value="HTML">HTML (&lt;b&gt;, &lt;i&gt;, &lt;a&gt;)</option>
            <option value="Markdown">Markdown</option>
          </select>
        </div>
      </section>

      <section className="card space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={withButton}
            onChange={(e) => setWithButton(e.target.checked)}
          />
          Додати кнопку (Mini App)
        </label>
        {withButton && (
          <div className="space-y-2">
            <input
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value)}
              placeholder="Текст кнопки"
              maxLength={64}
              className="w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
            />
            <input
              value={buttonUrl}
              onChange={(e) => setButtonUrl(e.target.value)}
              placeholder="https://..."
              className="w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
            />
            <p className="text-xs text-tg-hint">
              URL має бути HTTPS. Для Mini App підставляй ngrok-домен або production-домен.
            </p>
          </div>
        )}
      </section>

      <div className="flex gap-2">
        <button
          onClick={() => send(true)}
          disabled={!text.trim() || tooLong || busy}
          className="btn-secondary flex-1"
        >
          {busy ? '...' : '🧪 Тест собі'}
        </button>
        <button
          onClick={() => send(false)}
          disabled={!text.trim() || tooLong || busy}
          className="btn-primary flex-1"
        >
          {busy ? 'Розсилаю...' : '📢 Розіслати всім'}
        </button>
      </div>

      {error && (
        <div className="card border border-tg-destructive text-sm text-tg-destructive">
          {error}
        </div>
      )}

      {result && (
        <div className="card space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-tg-hint">
            Результат
          </h3>
          <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
            <ResultStat label="Всього" value={result.total} />
            <ResultStat label="Доставлено" value={result.sent} accent="success" />
            <ResultStat
              label="Заблокували"
              value={result.blocked}
              accent={result.blocked > 0 ? 'warning' : undefined}
              hint="bot blocked"
            />
            <ResultStat
              label="Помилки"
              value={result.failed}
              accent={result.failed > 0 ? 'destructive' : undefined}
            />
          </div>
          {result.errors.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-tg-hint">
                Перші {result.errors.length} помилок
              </summary>
              <ul className="mt-2 space-y-1">
                {result.errors.map((e, i) => (
                  <li key={i} className="rounded bg-tg-bg px-2 py-1">
                    <code className="text-tg-hint">{e.telegramId}:</code> {e.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <PushBroadcast />
      <DailyReminders />
    </div>
  );
}

// Масовий push у застосунок (Android FCM + Web Push). Окремий канал від Telegram.
function PushBroadcast() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<{ recipients: number; delivered: number; failed: number } | null>(
    null,
  );
  const [err, setErr] = useState<string | null>(null);

  async function send(testOnly: boolean) {
    if (busy || !title.trim() || !body.trim()) return;
    if (!testOnly && !confirm(`Надіслати push УСІМ, у кого ввімкнені сповіщення?`)) return;
    setBusy(true);
    setErr(null);
    setRes(null);
    try {
      const r = await api.admin.pushBroadcast({
        title: title.trim(),
        body: body.trim(),
        testOnly,
      });
      setRes(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Помилка');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-2">
      <h3 className="text-sm font-semibold">📲 Push у застосунок</h3>
      <p className="text-[11px] text-tg-hint">
        Летить на Android-додаток і браузери, де юзер дозволив сповіщення. Це не Telegram.
      </p>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Заголовок"
        maxLength={80}
        className="w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Текст сповіщення"
        rows={2}
        maxLength={300}
        className="w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
      />
      <div className="flex gap-2">
        <button
          onClick={() => send(true)}
          disabled={busy}
          className="btn-secondary flex-1 text-sm disabled:opacity-50"
        >
          Тест собі
        </button>
        <button
          onClick={() => send(false)}
          disabled={busy}
          className="btn-primary flex-1 text-sm disabled:opacity-50"
        >
          {busy ? 'Надсилаю…' : 'Надіслати всім'}
        </button>
      </div>
      {res && (
        <div className="text-xs text-tg-success">
          Отримувачів: {res.recipients} · доставлено: {res.delivered} · не вдалось: {res.failed}
        </div>
      )}
      {err && <div className="text-xs text-tg-destructive">{err}</div>}
    </div>
  );
}

type ReminderResult = NonNullable<Awaited<ReturnType<typeof api.admin.triggerDailyReminders>>>;
type Bucket = 'sent' | 'blocked' | 'failed' | null;

function DailyReminders() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ReminderResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState<Bucket>(null);

  async function trigger() {
    if (busy) return;
    if (!confirm('Запустити щоденне нагадування ВСІМ неактивним юзерам прямо зараз?')) return;
    setBusy(true);
    setError(null);
    setResult(null);
    setOpened(null);
    haptic.medium();
    try {
      const r = await api.admin.triggerDailyReminders();
      setResult(r);
      haptic.success();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Помилка');
      haptic.error();
    } finally {
      setBusy(false);
    }
  }

  function toggle(b: Bucket) {
    haptic.light();
    setOpened((cur) => (cur === b ? null : b));
  }

  return (
    <section className="card space-y-3">
      <div>
        <h3 className="text-sm font-semibold">📨 Щоденне нагадування</h3>
        <p className="mt-1 text-xs text-tg-hint">
          Шле повідомлення тим юзерам, які <strong>не заходили сьогодні</strong> і не отримували нагадування за останні 24 год. Текст рандомний з пулу варіантів. Авто-запуск щодня о 19:00 (Київ).
        </p>
      </div>
      <button onClick={trigger} disabled={busy} className="btn-primary w-full">
        {busy ? 'Шлемо…' : '📨 Запустити зараз'}
      </button>
      {error && <div className="text-xs text-tg-destructive">{error}</div>}
      {result && (
        <>
          <div className="grid grid-cols-4 gap-2 text-center">
            <ResultStat label="Кандидатів" value={result.candidates} />
            <ResultStatButton
              label="Надіслано"
              value={result.sent}
              accent="success"
              active={opened === 'sent'}
              onClick={() => toggle('sent')}
              disabled={result.sent === 0}
            />
            <ResultStatButton
              label="Заблоковані"
              value={result.blocked}
              accent="warning"
              active={opened === 'blocked'}
              onClick={() => toggle('blocked')}
              disabled={result.blocked === 0}
            />
            <ResultStatButton
              label="Помилки"
              value={result.failed}
              accent="destructive"
              active={opened === 'failed'}
              onClick={() => toggle('failed')}
              disabled={result.failed === 0}
            />
          </div>

          {opened === 'sent' && <UserList title="Надіслано" items={result.sentUsers} />}
          {opened === 'blocked' && <UserList title="Заблоковані" items={result.blockedUsers} />}
          {opened === 'failed' && <UserList title="Помилки" items={result.failedUsers} />}
        </>
      )}
    </section>
  );
}

function UserList({
  title,
  items,
}: {
  title: string;
  items: Array<{ id: string; telegramId: string | null; firstName: string | null; username: string | null; reason?: string }>;
}) {
  if (items.length === 0) {
    return <p className="text-xs text-tg-hint">Список порожній.</p>;
  }
  return (
    <div className="space-y-1 rounded-lg bg-tg-bg p-2">
      <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-tg-hint">
        {title} ({items.length})
      </div>
      <div className="max-h-72 space-y-1 overflow-y-auto">
        {items.map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-2 rounded bg-tg-secondary-bg px-2 py-1.5 text-xs">
            <div className="min-w-0 flex-1 truncate">
              <span className="font-medium">{u.firstName ?? 'Без імені'}</span>
              {u.username && <span className="text-tg-hint"> @{u.username}</span>}
              {u.reason && <span className="text-tg-destructive"> · {u.reason}</span>}
            </div>
            {u.telegramId && (
              <a
                href={`https://t.me/${u.username ?? ''}`}
                target="_blank"
                rel="noopener"
                className="shrink-0 text-tg-link"
              >
                tg:{u.telegramId.slice(-6)}
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ResultStatButton({
  label,
  value,
  accent,
  active,
  disabled,
  onClick,
}: {
  label: string;
  value: number;
  accent: 'success' | 'destructive' | 'warning';
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const cls =
    accent === 'success'
      ? 'text-tg-success'
      : accent === 'destructive'
        ? 'text-tg-destructive'
        : 'text-yellow-500';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg p-2 text-center transition disabled:opacity-50 ${
        active ? 'bg-tg-button/10 ring-2 ring-tg-button' : 'bg-tg-bg hover:bg-tg-secondary-bg'
      }`}
    >
      <div className={`text-lg font-semibold ${cls}`}>{value}</div>
      <div className="text-[10px] text-tg-hint">{label} {!disabled && '↓'}</div>
    </button>
  );
}

function ResultStat({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: number;
  accent?: 'success' | 'destructive' | 'warning';
  hint?: string;
}) {
  const cls =
    accent === 'success'
      ? 'text-tg-success'
      : accent === 'destructive'
        ? 'text-tg-destructive'
        : accent === 'warning'
          ? 'text-yellow-500'
          : '';
  return (
    <div className="rounded-lg bg-tg-bg p-2">
      <div className={`text-lg font-semibold ${cls}`}>{value}</div>
      <div className="text-[10px] text-tg-hint">{label}</div>
      {hint && <div className="text-[10px] text-tg-hint">{hint}</div>}
    </div>
  );
}
